import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Alert } from "../Alert";
import { Button } from "../Button";
import { LoadingSpinner } from "../LoadingSpinner";
import { isChunkLoadError, shouldAttemptAutoReload } from "utilities/chunkLoadRecovery";
import { isUnsafeToReload } from "utilities/buildRefreshGuard";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

type StaleBuildStatus = "reloading" | "unsafe" | "loop-guarded" | null;

interface State {
  hasError: boolean;
  error: Error | null;
  staleBuild: StaleBuildStatus;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      staleBuild: null,
    };
  }

  static getDerivedStateFromError(error: Error): Pick<State, "hasError" | "error"> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    if (!isChunkLoadError(error)) return;

    if (isUnsafeToReload()) {
      this.setState({ staleBuild: "unsafe" });
      return;
    }

    if (shouldAttemptAutoReload(window.BOON_BUILD_SHA ?? "unknown")) {
      this.setState({ staleBuild: "reloading" });
      window.location.reload();
      return;
    }

    this.setState({ staleBuild: "loop-guarded" });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      if (this.state.staleBuild === "reloading") {
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 gap-4">
            <LoadingSpinner />
            <p className="text-gray-600">Updating to the latest version…</p>
          </div>
        );
      }

      if (this.state.staleBuild === "unsafe") {
        return (
          <div className="flex items-center justify-center min-h-screen bg-gray-50 p-6">
            <div className="max-w-md w-full">
              <Alert variant="warning">
                A new version of Boon is available, but you have unsaved
                changes. Finish saving, then reload to get the latest
                version.
              </Alert>
              <Button
                customClasses={{ "mt-4 w-full": true }}
                onClick={() => window.location.reload()}
              >
                Reload now
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center mb-4">
              <svg
                className="w-6 h-6 text-red-500 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h2 className="text-xl font-semibold text-gray-900">
                Something went wrong
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              We're sorry, but something unexpected happened. Our team has been
              notified and is working to fix the issue.
            </p>
            {this.state.error && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Error details
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <Button customClasses={{ "w-full": true }} onClick={() => window.location.reload()}>
              Reload page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
